import { Dictionary } from 'lodash';
import { Currency } from 'api/account';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { DateType, Role, Team, User } from 'api/models';
import { ProjectFinancialsContractType } from 'api/project';
import {
  AllocationMedium,
  ResourceManagementConfig,
} from 'api/resource-management/types';
import { ProjectsWithMembers } from '../../types';

export interface Lookup {
  projectLookup: Dictionary<ProjectsWithMembers>;
  teamLookup: Dictionary<Team>;
  roleLookup: Dictionary<Role>;
  userLookup: Dictionary<User>;
  teamMembership: Record<string, Team[]>;
  teams: Team[];
  resourceManagementConfig?: ResourceManagementConfig;
}

export enum StrategyOptions {
  LoadBalanced = 'LOAD_BALANCED',
  MaximumMargin = 'MAXIMUM_MARGIN',
  Manual = 'MANUAL',
}

export enum StepsId {
  SelectProject = 0,
  LoadResources = 1,
  PlanTeam = 2,
  OptimizeFinancial = 3,
  Allocate = 4,
}
export const OtherTeamMembersRowId = -1;

export type ProjectDetails = {
  id: number;
  name: string;
  startDate: string;
  dueDate: string;
};

export type Step = {
  id: StepsId;
  text: string;
};

export type TeamType = Pick<Team, 'teamId' | 'teamName' | 'roleId'>;

export type StepsRowData = {
  team?: TeamType;
  user?: { userId: number } | null;
  allocationStartDate?: DateType;
  allocationEndDate?: DateType;
  allocatedMinutes: number;
  utilization?: number;
  simpleSearchFilter?: SimpleSearchFilter;
  margin?: number;
  cost?: number;
  profit?: number;
  revenue?: number;
  dateEditable?: boolean;
  resolved: boolean;
  medium?: AllocationMedium;
  isNoMatchFoundShown: boolean;
};

export type StepsData = {
  financialContractType?: ProjectFinancialsContractType;
  projectOwner?: { userId: number };
  projectCurrency?: Currency;
  projectBudget?: number;
  budgetedHours?: number;
  budgetedMinutes?: number;
  totalAllocatedHours?: number;
  projectId?: number;
  filter?: { optimiseFor: StrategyOptions };
  rows: StepsRowData[];
  cost?: number;
  margin?: number;
  profit?: number;
  revenue?: number;
  fixedFee?: number;
};

export type UpdateTeamMembers = {
  updatedRows: StepsRowData[];
  webSocketTrigger?: boolean;
  updatedRowId?: number;
};

export type TeamPlanGridData = {
  allocatedDates: {
    startDate?: DateType;
    endDate?: DateType;
  };
  allocatedMinutes?: number;
  teamMember?: number | null;
  id?: number;
  teamName?: string;
  teamId?: number | null;
  roleId?: number;
  simpleSearchFilter?: SimpleSearchFilter;
  isDatesEditable?: boolean;
  utilization?: number;
  isAssigned: boolean;
  isNoMatchFoundShown: boolean;
  isTeamPlanStep: boolean;
};

export type FinancialsGridData = {
  teamMember?: number | null;
  id?: number;
  teamName?: string;
  teamId?: number | null;
  roleId?: number;
  revenue?: number;
  cost?: number;
  margin?: number;
  allocatedMinutes?: number;
  allocationStartDate?: DateType;
  allocationEndDate?: DateType;
  isDatesEditable?: boolean;
  simpleSearchFilter?: SimpleSearchFilter;
  utilization?: number;
  isAssigned: boolean;
  isFinancialStep: boolean;
  isNoMatchFoundShown: boolean;
};

export type AllocateGridData = {
  teamMember?: number | null;
  id?: number;
  teamName?: string;
  teamId?: number | null;
  roleId?: number;
  revenue?: number;
  cost?: number;
  margin?: number;
  allocationStartDate?: DateType;
  allocationEndDate?: DateType;
  allocatedMinutes?: number;
  utilization?: number;
  allocationMedium?: AllocationMedium;
  isAllocationStep: boolean;
};
export type PlannerGridData =
  | TeamPlanGridData
  | FinancialsGridData
  | AllocateGridData;
